"use client";

import { useEffect, useState } from "react";
import styles from "./page.module.css";
import serviceService, { Service } from "@/services/serviceService";
import PaymentModal from "./components/PaymentModal";

const FEATURE_ICONS = [
  "/assets/sdtc-image/icon/ldea.svg",
  "/assets/sdtc-image/icon/shake-hand.svg",
  "/assets/sdtc-image/icon/Timing.svg",
  "/assets/sdtc-image/icon/presentation.svg",
  "/assets/sdtc-image/icon/human.svg",
  "/assets/sdtc-image/icon/target.svg",
];

const TYPE_ICONS = {
  web: "/assets/sdtc-image/icon/website.svg",
  app: "/assets/sdtc-image/icon/App.svg",
  agency: "/assets/sdtc-image/icon/Agency.svg",
};

type ServiceType = "web" | "app" | "agency";

export default function BangGiaPage() {
  const [activeTab, setActiveTab] = useState<ServiceType>("web");
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState<Service | null>(null);

  const typeNames = {
    web: "Website",
    app: "App",
    agency: "Agency",
  };

  useEffect(() => {
    fetchServices();
  }, [activeTab]);

  const fetchServices = async () => {
    try {
      setLoading(true);
      setError("");
      const response = await serviceService.getServices({
        type: activeTab,
        limit: 0,
      });
      setServices(response.data || []);
    } catch (err) {
      setError("Không thể tải danh sách dịch vụ. Vui lòng thử lại sau.");
      console.error("Error fetching services:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleStartPayment = (service: Service) => {
    setSelectedService(service);
    setIsPaymentModalOpen(true);
  };

  const handleClosePaymentModal = () => {
    setIsPaymentModalOpen(false);
    setSelectedService(null);
  };

  const handleTabClick = (type: ServiceType) => {
    setActiveTab(type);
  };

  const formatVND = (amount?: number) => {
    if (!amount || isNaN(amount as any)) return "Liên hệ";
    return `${new Intl.NumberFormat("vi-VN", { maximumFractionDigits: 0 }).format(amount)} đ`;
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>Bảng giá dịch vụ</h1>

        <div className={styles.tabButtons}>
          <button
            className={`${styles.tabButton} ${
              activeTab === "web" ? styles.active : ""
            }`}
            onClick={() => handleTabClick("web")}
          >
            Website
          </button>
          <button
            className={`${styles.tabButton} ${
              activeTab === "app" ? styles.active : ""
            }`}
            onClick={() => handleTabClick("app")}
          >
            App
          </button>
          <button
            className={`${styles.tabButton} ${
              activeTab === "agency" ? styles.active : ""
            }`}
            onClick={() => handleTabClick("agency")}
          >
            Agency
          </button>
        </div>
      </div>

      {loading ? (
        <div className={styles.loading}>Đang tải...</div>
      ) : error ? (
        <div className={styles.error}>{error}</div>
      ) : (
        <div className={styles.priceCards}>
          {services.length === 0 ? (
            <div className={styles.emptyState}>
              Chưa có dịch vụ nào trong danh mục này
            </div>
          ) : (
            services.map((service, index) => (
              <div key={service.id || index} className={styles.priceCard}>
                <div className={styles.cardIcon}>
                  <img
                    src={
                      service.imageUrl ||
                      service.image ||
                      TYPE_ICONS[service.type]
                    }
                    alt={service.title}
                  />
                </div>

                <div className={styles.cardHeader}>
                  <h3>{service.title || "Chưa đặt tên"}</h3>
                  <div className={styles.price}>
                    {formatVND(service.price)}
                  </div>
                </div>

                <button
                  className={styles.btnDetail}
                  onClick={() => handleStartPayment(service)}
                >
                  Bắt đầu
                </button>

                <div className={styles.featuresContainer}>
                  <ul className={styles.features}>
                    {Array.isArray(service.description) &&
                    service.description.length > 0
                      ? service.description.map((text, idx) => (
                          <li key={idx}>
                            <div className={styles.featureIconWrapper}>
                              <img
                                src={
                                  FEATURE_ICONS[idx % FEATURE_ICONS.length]
                                }
                                alt="Feature Icon"
                                className={styles.featureIcon}
                              />
                            </div>
                            <div className={styles.featureContent}>
                              <span className={styles.featureText}>
                                {text || "Chưa có mô tả"}
                              </span>
                            </div>
                          </li>
                        ))
                      : [
                          <li key={0}>
                            <div className={styles.featureIconWrapper}>
                              <img
                                src={FEATURE_ICONS[0]}
                                alt="Feature Icon"
                                className={styles.featureIcon}
                              />
                            </div>
                            <div className={styles.featureContent}>
                              <span className={styles.featureText}>
                                Chưa có mô tả
                              </span>
                            </div>
                          </li>,
                        ]}
                  </ul>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      <PaymentModal
        isOpen={isPaymentModalOpen}
        onClose={handleClosePaymentModal}
        service={selectedService}
      />
    </div>
  );
}
